import { Component, inject, Input, OnInit } from '@angular/core';
import { BlockUIService } from "ng-block-ui";
import { ApiCallService } from "../../../../services/api-call.service";
import { ErrorMessageService } from "../../../../services/error-message.service";
import { Router } from "@angular/router";
import {
  accountInfo,
  evaluatorRole,
  Session,
  UserRole
} from "../../../../services/session.service";
import { first } from "rxjs/operators";
import { RightMenuService } from "../../../../services/right-menu.service";
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-accounts-list',
    templateUrl: './accounts-list.component.html',
    styleUrl: './accounts-list.component.scss',
    imports: [MatIcon, MatTooltip]
})
export class AccountsListComponent implements OnInit {
  private readonly Session: Session = inject(Session);
  private readonly blockUI: BlockUIService = inject(BlockUIService);
  private readonly ApiCallService: ApiCallService = inject(ApiCallService);
  private readonly errorMessage: ErrorMessageService = inject(ErrorMessageService);
  private readonly route: Router = inject(Router);
  private readonly rightMenuService = inject(RightMenuService);

  hasOtherAccounts: boolean = false;
  currentEvaluatorProgramRequestKey: string = "";
  userOrgName: string;
  userOrgLogo: string;
  otherAccounts: accountInfo[] = [];
  portals:portalInfo[] = [];

  ngOnInit() {
    this.currentEvaluatorProgramRequestKey = this.Session.isEvaluatorWorkingForOtherInstitution();
    this.divideInPortals();
    const org = this.Session.getUserOrganization();
    this.userOrgName = org.name;
    this.userOrgLogo = org.topNavigationLogo;
  }

  divideInPortals() {
    const offlineAccreditor: string = "*Weave Accreditation - official collections";
    const allAccounts:accountInfo[] = this.Session.getOtherAccounts();
    this.portals = [];
    //first the reviewer roles
    const roles: evaluatorRole[] = this.Session.getEvaluatorRoles();
    roles.forEach( role => {
      let account: accountInfo = {
        accountName: role.programName + ", " + role.institutionName + " " + role.accreditor.abbreviation,
        accountRequestKey: role.program.requestKey,
        accredProjectName: role.program.name,
        accredProjectRequestKey: role.program.requestKey,
        accreditorName: role.accreditor.name,
        accreditorAbbr: role.accreditor.abbreviation,
        annualUpdateRequestKey: "",
        directoryInstitutionRequestKey: role.program.institutionDirectoryRequestKey,
        directoryProgramRequestKey: role.program.programDirectoryProgramRequestKey,
        userRole: UserRole.evaluator,
        nameToShow: role.program.name,
        isAccreditor: false,
        isFederation: false,
      }
      //exclude the current account
      if( account.accredProjectRequestKey != this.currentEvaluatorProgramRequestKey ) {
        allAccounts.push( account );
      }
    } );
    this.checkIfOtherAccounts(allAccounts);

    allAccounts.forEach((account) =>{
      if(account.accountName) {
        account.nameToShow = account.accountName;
      } else {
        account.nameToShow = account.accredProjectName;
      }
      if(account.accreditorAbbr) {
        account.nameToShow += ", " + account.accreditorAbbr;
      }
      if(!account.accreditorName || account.accreditorName === offlineAccreditor) {
        this.otherAccounts.push(account);
      } else {
        let portal = this.portals.find( p => p.name === account.accreditorAbbr );
        if( !portal ) {
          portal = {
            name: account.accreditorAbbr,
            accounts: []
          }
          this.portals.push( portal );
        }
        portal.accounts.push( account );
      }
    });
  }

  checkIfOtherAccounts(allAccounts:accountInfo[]): void {
    this.hasOtherAccounts = allAccounts.length > 0;
  }

  switchRole(accountRequestKey:string) {
    //first remove evaluator role
    if(this.currentEvaluatorProgramRequestKey) {
      this.ApiCallService.postJsonData("/aeAMSsetCurrentEvaluator/", {programRequestKey : null}).subscribe((response) => {
        if( response.success ) {
          //no need to do anything I guess...
        }
        else{
          this.errorMessage.showErrorModal("There was an error with the reviewer role", response.dataError);
        }
      });
    }
    this.blockUI.start("account-menu-ui");
    this.ApiCallService.postJsonData("/setAccountUser/", {accountRequestKey : accountRequestKey}).subscribe((response) =>{
      if(response.success){
        this.Session.reload().pipe(first()).subscribe((session) =>{
          this.blockUI.stop("account-menu-ui");
          this.rightMenuService.closeMenu();
          this.route.navigate(["/home"]);
        })
      }
      else{
        this.blockUI.stop("account-menu-ui");
        this.errorMessage.showErrorModal("There was an error switching this account", response.dataError);
      }
    })
  }

  setEvaluator(programRequestKey: string = null){
    this.blockUI.start("account-menu-ui");
    this.ApiCallService.postJsonData("/aeAMSsetCurrentEvaluator/", {programRequestKey : programRequestKey}).subscribe((response) =>{
      if(response.success){
        this.Session.reload().pipe(first()).subscribe((session) =>{
          this.blockUI.stop("account-menu-ui");
          this.rightMenuService.closeMenu();
          this.route.navigate(["/home"]);
        })
      }
      else{
        this.blockUI.stop("account-menu-ui");
        this.errorMessage.showErrorModal("There was an error with opening the program", response.dataError);
      }
    })
  }
}

export interface portalInfo {
  name: string;
  accounts: accountInfo[];
}
